import { type } from 'arktype';
import { MS_PER_DAY, dayKeyToUtcMs } from '../lib/day-key-math';
import { dayKeySchema } from './day-key.model';
import {
  acceptSuggestionSchema,
  confirmBookingSchema,
  convertMyWeekEntrySchema,
  createMyWeekAbsenceSchema,
  createMyWeekTimeSchema,
  updateMyWeekTimeSchema,
} from './my-week.model';
import type { MyWeekAbsenceTypeOption, MyWeekDay, MyWeekDayStats, MyWeekOpenStamp, MyWeekResponse } from './my-week.model';
import type { ReviewQueueSeverity } from './review-queue.model';
import type { DryRunObligationDelta } from './rule-catalog.model';
import { updateTimetrackingAbsenceSchema } from './timetracking-absence.model';

const OVERVIEW_RANGE_DAYS = 31;
const OVERVIEW_QUERY_ID_LIMIT = 100;
const OVERVIEW_FILTER_ID_LIMIT = 200;
const OVERVIEW_PAIR_LIMIT = 5000;
const OVERVIEW_BOOKING_TARGET_LIMIT = 20000;

function overviewRangeIsValid(dateFrom: string, dateTo: string): boolean {
  const spanMs = dayKeyToUtcMs(dateTo) - dayKeyToUtcMs(dateFrom);
  return spanMs >= 0 && spanMs <= (OVERVIEW_RANGE_DAYS - 1) * MS_PER_DAY;
}

function isDistinct(values: readonly string[]): boolean {
  return new Set(values).size === values.length;
}

function frozenBookingTargetCount(target: FrozenCommand): number {
  return target.kind === 'day' ? target.bookingIds.length : 1;
}

export const overviewIdSchema = type('1 <= string <= 128');

function distinctIdListSchema(limit: number) {
  return overviewIdSchema
    .array()
    .atMostLength(limit)
    .narrow((ids, ctx) => isDistinct(ids) || ctx.mustBe('an array of distinct IDs'));
}

export const overviewQuerySchema = type({
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  'teamIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  take: '1 <= number.integer <= 20',
  skip: 'number.integer >= 0',
})
  .narrow((query, ctx) => {
    if (!overviewRangeIsValid(query.dateFrom, query.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    const totalIds = (query.teamIds?.length ?? 0) + (query.locationIds?.length ?? 0) + (query.personIds?.length ?? 0);
    return totalIds <= OVERVIEW_QUERY_ID_LIMIT || ctx.mustBe('at most 100 selected IDs in total');
  })
  .onUndeclaredKey('reject');

export type OverviewQueryInput = typeof overviewQuerySchema.t;

export const overviewOptionsKindSchema = type("'teams' | 'locations' | 'persons'");

export type OverviewOptionsKind = typeof overviewOptionsKindSchema.t;

// Each kind consumes the other two dimensions; same-dimension IDs are ignored if sent.
export const overviewOptionsQuerySchema = type({
  'q?': 'string <= 200',
  'teamIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_QUERY_ID_LIMIT),
  take: '1 <= number.integer <= 20',
  skip: 'number.integer >= 0',
})
  .narrow((query, ctx) => {
    const totalIds = (query.teamIds?.length ?? 0) + (query.locationIds?.length ?? 0) + (query.personIds?.length ?? 0);
    return totalIds <= OVERVIEW_QUERY_ID_LIMIT || ctx.mustBe('at most 100 selected IDs in total');
  })
  .onUndeclaredKey('reject');

export type OverviewOptionsQueryInput = typeof overviewOptionsQuerySchema.t;

const overviewPreviewFilterSchema = type({
  selection: "'filter'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  'teamIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
  'locationIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
  'personIds?': distinctIdListSchema(OVERVIEW_FILTER_ID_LIMIT),
})
  .narrow((value, ctx) => overviewRangeIsValid(value.dateFrom, value.dateTo) || ctx.mustBe('a date range of at most 31 ordered days'))
  .onUndeclaredKey('reject');

const overviewPreviewDayTargetSchema = type({
  personId: overviewIdSchema,
  dates: dayKeySchema
    .array()
    .atLeastLength(1)
    .narrow((dates, ctx) => isDistinct(dates) || ctx.mustBe('an array of distinct dates')),
}).onUndeclaredKey('reject');

const overviewPreviewDaysSchema = type({
  selection: "'days'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  targets: overviewPreviewDayTargetSchema.array().atLeastLength(1),
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    if (!isDistinct(value.targets.map(target => target.personId))) return ctx.mustBe('targets with distinct personIds');
    const inRange = value.targets.every(target => target.dates.every(date => date >= value.dateFrom && date <= value.dateTo));
    return inRange || ctx.mustBe('target dates inside the stated range');
  })
  .onUndeclaredKey('reject');

const overviewPreviewAdoptPlanSchema = type({
  selection: "'adopt_plan'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  personId: overviewIdSchema,
  date: dayKeySchema,
  bookingId: overviewIdSchema,
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    return (value.date >= value.dateFrom && value.date <= value.dateTo) || ctx.mustBe('a target date inside the stated range');
  })
  .onUndeclaredKey('reject');

const overviewPreviewAcceptSuggestionSchema = type({
  selection: "'accept_suggestion'",
  dateFrom: dayKeySchema,
  dateTo: dayKeySchema,
  personId: overviewIdSchema,
  date: dayKeySchema,
  sourceBookingId: overviewIdSchema,
  sourceDate: dayKeySchema,
})
  .narrow((value, ctx) => {
    if (!overviewRangeIsValid(value.dateFrom, value.dateTo)) return ctx.mustBe('a date range of at most 31 ordered days');
    return (value.date >= value.dateFrom && value.date <= value.dateTo) || ctx.mustBe('a target date inside the stated range');
  })
  .onUndeclaredKey('reject');

export const overviewPreviewRequestSchema = overviewPreviewFilterSchema
  .or(overviewPreviewDaysSchema)
  .or(overviewPreviewAdoptPlanSchema)
  .or(overviewPreviewAcceptSuggestionSchema);

export type OverviewPreviewRequestInput = typeof overviewPreviewRequestSchema.t;

export const overviewRevisionsSchema = type({
  workRevision: '1 <= string <= 128',
  configRevision: '1 <= string <= 128',
}).onUndeclaredKey('reject');

export type Revisions = typeof overviewRevisionsSchema.t;

const frozenDayCommandSchema = type({
  kind: "'day'",
  bookingIds: overviewIdSchema.array().narrow((ids, ctx) => isDistinct(ids) || ctx.mustBe('an array of distinct booking IDs')),
}).onUndeclaredKey('reject');

const frozenAdoptPlanCommandSchema = type({
  kind: "'adopt_plan'",
  bookingId: overviewIdSchema,
}).onUndeclaredKey('reject');

const frozenAcceptSuggestionCommandSchema = type({
  kind: "'accept_suggestion'",
  sourceBookingId: overviewIdSchema,
  sourceDate: dayKeySchema,
}).onUndeclaredKey('reject');

export const frozenCommandSchema = frozenDayCommandSchema.or(frozenAdoptPlanCommandSchema).or(frozenAcceptSuggestionCommandSchema);

export type FrozenCommand = typeof frozenCommandSchema.t;

const frozenDayTargetSchema = type({
  date: dayKeySchema,
  target: frozenCommandSchema,
}).onUndeclaredKey('reject');

export const frozenPersonSchema = type({
  personId: overviewIdSchema,
  expected: overviewRevisionsSchema,
  days: frozenDayTargetSchema.array().atLeastLength(1),
})
  .narrow((person, ctx) => isDistinct(person.days.map(day => day.date)) || ctx.mustBe('days with distinct dates'))
  .onUndeclaredKey('reject');

export type FrozenPerson = typeof frozenPersonSchema.t;

export const overviewConfirmRequestSchema = type({
  serverTodayKey: dayKeySchema,
  targets: frozenPersonSchema.array().atLeastLength(1),
  force: 'boolean',
})
  .narrow((request, ctx) => {
    if (!isDistinct(request.targets.map(target => target.personId))) return ctx.mustBe('targets with distinct personIds');
    const dates = request.targets.flatMap(target => target.days.map(day => day.date));
    let minDate = '';
    let maxDate = '';
    for (const date of dates) {
      if (minDate === '' || date < minDate) minDate = date;
      if (maxDate === '' || date > maxDate) maxDate = date;
    }
    if (dayKeyToUtcMs(maxDate) - dayKeyToUtcMs(minDate) > (OVERVIEW_RANGE_DAYS - 1) * MS_PER_DAY)
      return ctx.mustBe('targets within a single 31-day span');
    if (dates.length > OVERVIEW_PAIR_LIMIT) return ctx.mustBe('at most 5000 person/day pairs');
    const bookingTargets = request.targets.flatMap(target => target.days.map(day => frozenBookingTargetCount(day.target)));
    const bookingTotal = bookingTargets.reduce((sum, count) => sum + count, 0);
    return bookingTotal <= OVERVIEW_BOOKING_TARGET_LIMIT || ctx.mustBe('at most 20000 booking targets');
  })
  .onUndeclaredKey('reject');

export type OverviewConfirmRequestInput = typeof overviewConfirmRequestSchema.t;

export interface OverviewDayReview {
  pendingReviewCount: number;
  hasPendingDelete: boolean;
  latestCompleteEvaluation: {
    severity: ReviewQueueSeverity | null;
    issues: NonNullable<MyWeekDayStats['issues']>;
  } | null;
  complete: boolean;
}

export interface OverviewPerson {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface OverviewLocationEntry {
  name: string;
  icon: string | null;
  color: string | null;
  imageUrl: string | null;
}

export interface OverviewPersonGroup {
  person: OverviewPerson;
  days: MyWeekDay[];
  reviewByDate: Record<string, OverviewDayReview>;
  totals: MyWeekResponse['totals'];
  absenceTypes: MyWeekAbsenceTypeOption[];
  openStamp: MyWeekOpenStamp | null;
  revisions: Revisions;
  locationByDate: Record<string, OverviewLocationEntry>;
}

export interface OverviewResponse {
  data: OverviewPersonGroup[];
  meta: { take: number; skip: number; total: number; hasMore: boolean };
  serverTodayKey: string;
}

export interface OverviewOption {
  id: string;
  label: string;
  // Persons carry URL or null; teams/locations always null (frontend falls back to initials).
  avatarUrl: string | null;
}

export interface OptionsResponse {
  data: OverviewOption[];
  meta: { take: number; skip: number; total: number; hasMore: boolean; universeTotal: number };
  serverTodayKey: string;
}

export const previewEligibilities = ['eligible_clean', 'eligible_warning', 'force_eligible', 'unforceable'] as const;
export type PreviewEligibility = (typeof previewEligibilities)[number];

export interface OverviewPreviewIssue {
  key: string;
  severity: ReviewQueueSeverity;
  overridable: boolean;
  params: Record<string, string | number> | null;
}

// R-9a: alias of the domain obligation-delta contract DryRunObligationDelta.
export type DomainObligationDelta = DryRunObligationDelta;

export interface OverviewPreviewDay {
  date: string;
  target: FrozenCommand;
  eligibility: PreviewEligibility;
  suggestionCandidate?: { startTime: string; endTime: string };
  skipReasons: string[];
  issues: OverviewPreviewIssue[];
  obligationDeltas: DomainObligationDelta[];
}

export interface OverviewPreviewPerson {
  personId: string;
  personName: string;
  expected: Revisions;
  days: OverviewPreviewDay[];
}

export interface OverviewPreview {
  serverTodayKey: string;
  complete: boolean;
  incompleteReasons: string[];
  counts: {
    matchedPersons: number;
    matchedDays: number;
    candidateBookings: number;
    eligibleCleanDays: number;
    eligibleWarningDays: number;
    forceEligibleDays: number;
    unforceableDays: number;
  };
  persons: OverviewPreviewPerson[];
}

export type OverviewConfirmPersonOutcome = 'committed' | 'unchanged' | 'skipped' | 'conflict' | 'failed';
export type OverviewConfirmDayOutcome = 'changed' | 'unchanged' | 'skipped' | 'conflict' | 'failed';

export interface OverviewConfirmDayResult {
  date: string;
  outcome: OverviewConfirmDayOutcome;
  reasons: string[];
}

export interface OverviewConfirmPersonResult {
  personId: string;
  outcome: OverviewConfirmPersonOutcome;
  code?: string;
  days: OverviewConfirmDayResult[];
}

export interface OverviewConfirmResult {
  changedDayCount: number;
  unchangedDayCount: number;
  skippedDayCount: number;
  conflictedDayCount: number;
  failedDayCount: number;
  persons: OverviewConfirmPersonResult[];
}

export const overviewUnlockDayBodySchema = type({ 'expected?': overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewUnlockDayBodyInput = typeof overviewUnlockDayBodySchema.t;

export const overviewDraftCorrectionBodySchema = confirmBookingSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewDraftCorrectionBodyInput = typeof overviewDraftCorrectionBodySchema.t;

export const overviewCreateTimeBodySchema = createMyWeekTimeSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewCreateTimeBodyInput = typeof overviewCreateTimeBodySchema.t;

export const overviewUpdateTimeBodySchema = updateMyWeekTimeSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewUpdateTimeBodyInput = typeof overviewUpdateTimeBodySchema.t;

export const overviewVoidTimeBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewVoidTimeBodyInput = typeof overviewVoidTimeBodySchema.t;

export const overviewAbortDeleteBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewAbortDeleteBodyInput = typeof overviewAbortDeleteBodySchema.t;

export const overviewDiscardStampBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewDiscardStampBodyInput = typeof overviewDiscardStampBodySchema.t;

export const overviewCapturePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewCapturePlanBodyInput = typeof overviewCapturePlanBodySchema.t;

export const overviewDismissSuggestionBodySchema = acceptSuggestionSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewDismissSuggestionBodyInput = typeof overviewDismissSuggestionBodySchema.t;

export const overviewCreateAbsenceBodySchema = createMyWeekAbsenceSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewCreateAbsenceBodyInput = typeof overviewCreateAbsenceBodySchema.t;

export const overviewRemoveAbsenceBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewRemoveAbsenceBodyInput = typeof overviewRemoveAbsenceBodySchema.t;

export const overviewChangeAbsenceBodySchema = updateTimetrackingAbsenceSchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewChangeAbsenceBodyInput = typeof overviewChangeAbsenceBodySchema.t;

export const overviewConvertBodySchema = convertMyWeekEntrySchema
  .and(type({ expected: overviewRevisionsSchema }))
  .onUndeclaredKey('reject');

export type OverviewConvertBodyInput = typeof overviewConvertBodySchema.t;

export const overviewExcludePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewExcludePlanBodyInput = typeof overviewExcludePlanBodySchema.t;

export const overviewIncludePlanBodySchema = type({ expected: overviewRevisionsSchema }).onUndeclaredKey('reject');

export type OverviewIncludePlanBodyInput = typeof overviewIncludePlanBodySchema.t;
